import {
  BodyText,
  Callout,
  HeaderText,
  Separator,
  toast,
} from "@/components/ui";
import { useGetGenericObservations } from "@/network/useGenericObservations";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Fragment, useCallback } from "react";
import { GenericObservationEditor } from "../../components/GenericObservationEditor";
import { SectionContent } from "../../components/SectionContent";
import { SectionHeader } from "../../components/SectionHeader";
import { useSendQuestionnaire } from "../network";
import {
  HealthMaintenanceScreeningsSkeleton,
  HealthMaintenanceSocialSkeleton,
} from "./HealthMaintenanceSkeleton";
import { HealthScreeningSection } from "./HealthScreeningSection";

import {
  QuestionnaireCategory,
  useGetQuestionnaire,
} from "@/network/useQuestionnaires";
import type {
  LifestyleQuestionnaireTypes,
  MentalHealthQuestionnaireTypes,
} from "@/types/questionnaires";
import { QuestionnaireTableRow } from "./QuestionnaireTableRow";

const GROUP_IDENTIFIER = "social-health";

// Helper function to derive title from group identifier
const getTitleFromGroupIdentifier = (groupIdentifier: string): string => {
  return groupIdentifier
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Data structure for health screening sections
const HEALTH_SCREENING_SECTIONS = [
  { groupIdentifier: "breast-cancer" },
  { groupIdentifier: "cervical-cancer" },
  { groupIdentifier: "cholesterol" },
  { groupIdentifier: "colorectal-cancer" },
  { groupIdentifier: "diabetes" },
  { groupIdentifier: "sexually-transmitted-illnesses" },
] as const;

export const HealthMaintenanceScreenings = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { openQuestionnaire } = useSearch({
    strict: false,
  });
  const navigate = useNavigate();

  const {
    data: lifestyleData,
    isLoading: isLoadingLifestyleData,
    isError: isLifestyleDataError,
    refetch: refetchLifestyleData,
  } = useGetQuestionnaire(patientId, QuestionnaireCategory.LIFESTYLE);

  const {
    data: mentalHealthData,
    isLoading: isLoadingMentalHealthData,
    isError: isMentalHealthDataError,
    refetch: refetchMentalHealthData,
  } = useGetQuestionnaire(patientId, QuestionnaireCategory.MENTAL_HEALTH);

  const {
    data: healthMaintenanceItems,
    isLoading: isLoadingSocial,
    isError: isSocialError,
    refetch: refetchSocial,
  } = useGetGenericObservations(patientId, GROUP_IDENTIFIER);

  const sendQuestionnaireMutation = useSendQuestionnaire({
    patientId,
    onSuccess: () => {
      toast.success("Questionnaire sent successfully");
    },
    onError: (error) => {
      toast.error(`Failed to send questionnaire: ${error.message}`);
    },
  });

  const handleSendQuestionnaire = useCallback(
    (
      questionnaireType:
        | LifestyleQuestionnaireTypes
        | MentalHealthQuestionnaireTypes,
      questionnaireName: string,
      patientId: string,
    ) => {
      sendQuestionnaireMutation.mutate({
        questionnaireId: questionnaireType,
        questionnaireName: questionnaireName,
        patientId: patientId,
      });
    },
    [sendQuestionnaireMutation.mutate],
  );

  const handleAutoOpenComplete = useCallback(() => {
    // Clear the openQuestionnaire parameter from the URL
    navigate({
      to: "/patient/$patientId/health-maintenance",
      params: { patientId },
      search: { openQuestionnaire: undefined },
      replace: true,
    });
  }, [navigate, patientId]);

  const isSocialDataLoadError =
    !isLoadingSocial && (isSocialError || !healthMaintenanceItems);
  const isSocialDataLoadSuccess =
    !isLoadingSocial &&
    !isSocialDataLoadError &&
    healthMaintenanceItems &&
    healthMaintenanceItems.items.length > 0;

  return (
    <div className="w-full h-full flex flex-col mt-5">
      <SectionHeader title="Screenings" showSeparator={false} />
      <SectionContent>
        <div className="flex mt-1 mb-3 pl-3">
          <BodyText bold size="sm" textColor="tertiary" className="w-[30%]">
            Type
          </BodyText>
          <BodyText bold size="sm" textColor="tertiary" className="w-[15%]">
            Result
          </BodyText>
          <BodyText bold size="sm" textColor="tertiary" className="w-[25%]">
            Assigned
          </BodyText>
          <BodyText bold size="sm" textColor="tertiary" className="w-[30%]">
            Status
          </BodyText>
        </div>

        <div className="h-10 bg-secondary p-3">
          <HeaderText tag="h3" size="sm">
            Lifestyle
          </HeaderText>
        </div>
        {isLifestyleDataError && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load lifestyle data!"
              description="Please click here to try again."
              onClick={() => refetchLifestyleData()}
            />
            <Separator />
          </div>
        )}
        {isLoadingLifestyleData && (
          <HealthMaintenanceScreeningsSkeleton rowCount={5} />
        )}
        {lifestyleData?.map((questionnaireResponseData) => (
          <QuestionnaireTableRow
            key={questionnaireResponseData.questionnaireId}
            {...questionnaireResponseData}
            allQuestionnaires={lifestyleData ?? []}
            onSend={handleSendQuestionnaire}
            isSending={
              sendQuestionnaireMutation.isPending &&
              sendQuestionnaireMutation.variables?.questionnaireId ===
                questionnaireResponseData.questionnaireId
            }
            openQuestionnaire={openQuestionnaire}
            onAutoOpenComplete={handleAutoOpenComplete}
          />
        ))}

        <div className="h-10 bg-secondary p-3">
          <HeaderText tag="h3" size="sm">
            Mental Health
          </HeaderText>
        </div>
        {isLoadingMentalHealthData && (
          <HealthMaintenanceScreeningsSkeleton rowCount={4} />
        )}
        {isMentalHealthDataError && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load mental health data!"
              description="Please click here to try again."
              onClick={() => refetchMentalHealthData()}
            />
            <Separator />
          </div>
        )}

        {mentalHealthData?.map((questionnaireResponseData) => (
          <QuestionnaireTableRow
            key={questionnaireResponseData.questionnaireId}
            {...questionnaireResponseData}
            allQuestionnaires={mentalHealthData ?? []}
            onSend={handleSendQuestionnaire}
            isSending={
              sendQuestionnaireMutation.isPending &&
              sendQuestionnaireMutation.variables?.questionnaireId ===
                questionnaireResponseData.questionnaireId
            }
            openQuestionnaire={openQuestionnaire}
            onAutoOpenComplete={handleAutoOpenComplete}
          />
        ))}

        {HEALTH_SCREENING_SECTIONS.map((section) => (
          <Fragment key={section.groupIdentifier}>
            <div className="h-10 bg-secondary p-3">
              <HeaderText tag="h3" size="sm">
                {getTitleFromGroupIdentifier(section.groupIdentifier)}
              </HeaderText>
            </div>
            <HealthScreeningSection
              patientId={patientId}
              groupIdentifier={section.groupIdentifier}
            />
          </Fragment>
        ))}

        <div className="h-10 bg-secondary p-3">
          <HeaderText tag="h3" size="sm">
            Social
          </HeaderText>
        </div>
        {isSocialDataLoadError && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load health maintenance data!"
              description="Please click here to try again."
              onClick={() => refetchSocial()}
            />
            <Separator />
          </div>
        )}
        {isLoadingSocial && <HealthMaintenanceSocialSkeleton />}
        {isSocialDataLoadSuccess &&
          healthMaintenanceItems.items.map((item) => (
            <Fragment key={item.observationCode}>
              <GenericObservationEditor
                item={item}
                itemCategory="social-history"
                group={GROUP_IDENTIFIER}
              />
            </Fragment>
          ))}
      </SectionContent>
    </div>
  );
};
